import update from 'immutability-helper';
import { ActionType, defaultAction } from 'src/redux/types';
import { replaceSpaceWithEmptyString } from 'src/utilities//helpers';
import { Label } from '../constants';
import {
  FileCreateVaData,
  LimitCreateVaData,
  ListIdData,
  ResponseSubmitData,
  ResponseValidationData,
  TableFileCreateVaData,
} from '../types';
import { ACTION_TYPES } from './types';

export type CreateVaLimitState = {
  isLoading: boolean;
  data: LimitCreateVaData;
};

export type CreateVaDashboardState = {
  fileData: TableFileCreateVaData[];
  fileDataError: TableFileCreateVaData[];
  fileDataErrorOriginal: TableFileCreateVaData[];
  responseValidationError: ResponseValidationData[];
  listPartnerUserId: ListIdData[];
  listPartnerTxId: ListIdData[];
  listPartnerUserIdErrorData: ListIdData[];
  listPartnerTxIdErrorData: ListIdData[];
  isDataValidated: boolean;
  isValidationFinished: boolean;
  isDataTableInputError: boolean;
  enabledModalValidationButton: boolean;
  isModalValidateButtonClicked: boolean;
  totalInvalidRow: number;
  totalEmptyInput: number;
};

export type CreateVaSubmitState = {
  fileData: FileCreateVaData[];
  fileDataError: FileCreateVaData[];
  responseSubmitError: ResponseSubmitData[];
  hasNotifyFailedCreateVa: boolean;
};

export type CreateVaState = {
  dashboard: CreateVaDashboardState;
  limit: CreateVaLimitState;
  submit: CreateVaSubmitState;
};

const initialState: CreateVaState = {
  dashboard: {
    fileData: [],
    fileDataError: [],
    fileDataErrorOriginal: [],
    responseValidationError: [],
    listPartnerUserId: [],
    listPartnerTxId: [],
    listPartnerUserIdErrorData: [],
    listPartnerTxIdErrorData: [],
    isDataValidated: false,
    isValidationFinished: false,
    isDataTableInputError: false,
    enabledModalValidationButton: false,
    isModalValidateButtonClicked: false,
    totalInvalidRow: 0,
    totalEmptyInput: 0,
  },
  limit: {
    isLoading: false,
    data: {
      username: '',
      max_quota: 0,
      remaining_quota: 0,
    },
  },
  submit: {
    fileData: [],
    fileDataError: [],
    responseSubmitError: [],
    hasNotifyFailedCreateVa: false,
  },
};

export default (state = initialState, action: ActionType = defaultAction) => {
  const { payload, type } = action;
  switch (type) {
    case ACTION_TYPES.SET_FILE_DATA:
      return update(state, {
        dashboard: {
          fileData: {
            $set: payload,
          },
        },
      });
    case ACTION_TYPES.SET_FILE_DATA_ERROR: {
      const { newFileDataError, newFileDataErrorOriginal } = payload;
      if (newFileDataErrorOriginal) {
        return update(state, {
          dashboard: {
            $merge: {
              fileDataError: newFileDataError,
              fileDataErrorOriginal: newFileDataErrorOriginal,
            },
          },
        });
      }
      return update(state, {
        dashboard: {
          fileDataError: {
            $set: newFileDataError,
          },
        },
      });
    }
    case ACTION_TYPES.SET_LIST_PARTNER_USER_ID_DATA:
      return update(state, {
        dashboard: {
          $merge: {
            listPartnerUserId: payload,
            listPartnerUserIdErrorData: payload,
          },
        },
      });
    case ACTION_TYPES.SET_LIST_PARTNER_TX_ID_DATA:
      return update(state, {
        dashboard: {
          $merge: {
            listPartnerTxId: payload,
            listPartnerTxIdErrorData: payload,
          },
        },
      });
    case ACTION_TYPES.SET_LIST_PARTNER_USER_ID_ERROR_DATA:
      return update(state, {
        dashboard: {
          listPartnerUserIdErrorData: {
            $set: payload,
          },
        },
      });
    case ACTION_TYPES.SET_LIST_PARTNER_TX_ID_ERROR_DATA:
      return update(state, {
        dashboard: {
          listPartnerTxIdErrorData: {
            $set: payload,
          },
        },
      });
    case ACTION_TYPES.SET_IS_DATA_TABLE_INPUT_ERROR:
      return update(state, {
        dashboard: {
          isDataTableInputError: {
            $set: payload,
          },
        },
      });
    case ACTION_TYPES.SET_ENABLED_MODAL_VALIDATE_BUTTON:
      return update(state, {
        dashboard: {
          enabledModalValidationButton: {
            $set: payload,
          },
        },
      });
    case ACTION_TYPES.SET_LIST_PARTNER_USER_AND_TX_ID_FROM_ERROR_DATA:
      return update(state, {
        dashboard: {
          $merge: {
            listPartnerUserId: state.dashboard.listPartnerUserIdErrorData,
            listPartnerTxId: state.dashboard.listPartnerTxIdErrorData,
          },
        },
      });
    case ACTION_TYPES.SET_IS_DATA_VALIDATED:
      return update(state, {
        dashboard: {
          isDataValidated: {
            $set: payload,
          },
        },
      });
    case ACTION_TYPES.SET_IS_VALIDATION_FINISHED: {
      const newFileData = state.dashboard.fileData.map(
        (file: TableFileCreateVaData) => {
          const data = file;
          if (payload) {
            if (
              !data.is_open ||
              replaceSpaceWithEmptyString(data.is_open).length === 0
            ) {
              data.is_open = Label.rowData.open;
            }
            if (
              !data.is_single_use ||
              replaceSpaceWithEmptyString(data.is_single_use).length === 0
            ) {
              data.is_single_use = Label.rowData.multipleUse;
            }
            if (
              !data.trx_counter ||
              (typeof data.trx_counter === 'string' &&
                data.trx_counter.length === 0)
            ) {
              data.trx_counter = -1;
            }
            if (
              !data.is_lifetime ||
              replaceSpaceWithEmptyString(data.is_lifetime).length === 0
            ) {
              data.is_lifetime = Label.rowData.nonLifetime;
            }
          }
          return data;
        },
      );
      return update(state, {
        dashboard: {
          $merge: {
            isDataValidated: payload,
            isValidationFinished: payload,
            fileData: newFileData,
          },
        },
      });
    }
    case ACTION_TYPES.SET_RESPONSE_VALIDATION_DATA_SUCCESS: {
      const newFileData = state.dashboard.fileData.map(
        (data: TableFileCreateVaData) => {
          const successValidatedData = payload.find(
            (i: ResponseValidationData) => i.row_id === data.row_id,
          );
          if (successValidatedData) {
            return {
              ...data,
              edited: false,
              success: !data.isError && !data.isWarning,
            };
          }
          return data;
        },
      );
      return update(state, {
        dashboard: {
          fileData: {
            $set: newFileData,
          },
        },
      });
    }
    case ACTION_TYPES.SET_RESPONSE_VALIDATION_DATA_ERROR: {
      const { responseValidationError, isReset, fileWarning } = payload;

      if (isReset) {
        return update(state, {
          dashboard: {
            $merge: {
              fileDataError: initialState.dashboard.fileDataError,
              responseValidationError:
                initialState.dashboard.responseValidationError,
              totalInvalidRow: 0,
              totalEmptyInput: 0,
            },
          },
        });
      }

      let newFileDataErrorFromResponseValidationError =
        state.dashboard.fileData.filter(data => {
          const responseValidationErrorData = responseValidationError.find(
            (r: ResponseValidationData) => r.row_id === data.row_id,
          );
          return responseValidationErrorData || data.isError;
        });

      newFileDataErrorFromResponseValidationError =
        newFileDataErrorFromResponseValidationError.map(
          (data: TableFileCreateVaData) => {
            const responseValidationErrorData = responseValidationError.find(
              (r: ResponseValidationData) => r.row_id === data.row_id,
            );

            return {
              ...data,
              isError: responseValidationErrorData
                ? !responseValidationErrorData.success
                : data.isError,
            };
          },
        );

      const newFileDataErrorFromFileWarning =
        state.dashboard.fileData.filter(data => {
          const fileWarningData = fileWarning
            ? fileWarning.find(
                (d: TableFileCreateVaData) => d.row_id === data.row_id,
              )
            : {};
          return fileWarningData && fileWarningData.row_id === data.row_id;
        }) || [];

      const newFileWarning = newFileDataErrorFromFileWarning.filter(
        (item: TableFileCreateVaData) => {
          const alreadyOnFileDataError =
            newFileDataErrorFromResponseValidationError.find(
              errorData => errorData.row_id === item.row_id,
            );
          return !alreadyOnFileDataError;
        },
      );

      const newFileDataError = fileWarning
        ? newFileDataErrorFromResponseValidationError.concat(newFileWarning)
        : newFileDataErrorFromResponseValidationError;

      return update(state, {
        dashboard: {
          $merge: {
            fileDataError: newFileDataError,
            fileDataErrorOriginal: newFileDataError,
            responseValidationError,
            totalInvalidRow: newFileDataErrorFromResponseValidationError.length,
            totalEmptyInput: newFileWarning.length,
          },
        },
      });
    }
    case ACTION_TYPES.SET_IS_MODAL_VALIDATE_BUTTON_CLICKED: {
      // Set everything on the fileDataError that has been edited, into fileData
      const newFileData: TableFileCreateVaData[] = state.dashboard.fileData.map(
        (data: TableFileCreateVaData) => {
          const errorData = state.dashboard.fileDataError.find(
            (i: TableFileCreateVaData) => i.row_id === data.row_id,
          );
          if (errorData && data.row_id === errorData.row_id) {
            return {
              ...errorData,
              edited: false,
            };
          }
          return data;
        },
      );
      return update(state, {
        dashboard: {
          fileData: {
            $set: newFileData,
          },
        },
      });
    }
    case ACTION_TYPES.RESET_UPLOADED_DATA:
      return update(state, {
        dashboard: {
          $set: initialState.dashboard,
        },
      });
    case ACTION_TYPES.RESET_ALL_DATA_VALIDATED:
      return update(state, {
        dashboard: {
          $merge: {
            isDataValidated: false,
            isValidationFinished: false,
          },
        },
      });
    case ACTION_TYPES.GET_LIMIT_CREATION_VA:
      return update(state, {
        limit: {
          isLoading: {
            $set: true,
          },
        },
      });
    case ACTION_TYPES.GET_LIMIT_CREATION_VA_SUCCESS:
      return update(state, {
        limit: {
          $merge: {
            isLoading: false,
            data: payload.data,
          },
        },
      });
    case ACTION_TYPES.GET_LIMIT_CREATION_VA_FAILED:
      return update(state, {
        limit: {
          isLoading: {
            $set: false,
          },
        },
      });
    case ACTION_TYPES.SET_FILE_SUBMIT: {
      const newFileData = payload.map(
        (data: FileCreateVaData, index: number) => {
          return {
            ...data,
            row_number: index + 1,
          };
        },
      );
      return update(state, {
        submit: {
          fileData: {
            $set: newFileData,
          },
        },
      });
    }
    case ACTION_TYPES.SET_RESPONSE_SUBMIT_DATA_ERROR: {
      const newFileDataErrorFromResponseSubmitError =
        state.submit.fileData.filter((data: FileCreateVaData) => {
          const responseErrorData = payload.find(
            (r: ResponseValidationData) =>
              r.row_id === data.row_id || r.row_id === data.partner_user_id,
          );
          return responseErrorData;
        });

      return update(state, {
        submit: {
          $merge: {
            fileDataError: newFileDataErrorFromResponseSubmitError,
            responseSubmitError: payload,
          },
        },
      });
    }
    case ACTION_TYPES.SET_TOTAL_INVALID_ROW_AND_EMPTY_INPUT: {
      const { totalInvalidRow, totalEmptyInput } = payload;

      return update(state, {
        dashboard: {
          $merge: {
            totalInvalidRow,
            totalEmptyInput,
          },
        },
      });
    }
    case ACTION_TYPES.SET_HAS_NOTIFY_FAILED_CREATE_VA:
      return update(state, {
        submit: {
          hasNotifyFailedCreateVa: {
            $set: payload,
          },
        },
      });
    case ACTION_TYPES.UPDATE_RESPONSE_VALIDATION_ERROR_DATA:
      return update(state, {
        dashboard: {
          responseValidationError: {
            $set: payload,
          },
        },
      });
    default:
      return state;
  }
};
